// Editable · TextArea
//
// Wraps AntD <Input.TextArea> with DS tokens for consistent focus/border/
// disabled states across the editable tier. Mirrors @ds/editable/Input where
// sensible, but for multi-line free text. Normalises `onChange` to emit a
// plain `string` (not a ChangeEvent).
//
// AntD wrap justification (Rule 3 — non-trivial I/O surface): the `onChange`
// event-shape normalisation + the testid-reaches-the-fillable-element contract
// are easy to get wrong per-consumer. One wrapper, one correct implementation.
// The `data-testid` is forwarded onto the inner <textarea> (via AntD rest
// props) — NOT a wrapper span — so `getByTestId(x).fill()` works.

import type React from "react";
import { Input as AntInput } from "antd";
import type { TextAreaProps as AntTextAreaProps } from "antd/es/input";

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface TextAreaInput {
  value?: string;
  /** Placeholder text. */
  placeholder?: string;
  /** Disable the textarea. Default false. */
  disabled?: boolean;
  /** Number of visible text rows. Default 3. */
  rows?: number;
  /**
   * Max character count. Renders a character counter when provided.
   * Passed as AntD `maxLength`.
   */
  maxLength?: number;
  /** Auto-focus on mount — for inline-edit affordances. */
  autoFocus?: boolean;
  /** Validation status — passes through to AntD's status prop. */
  status?: "" | "warning" | "error";
  /** `id` forwarded to the underlying textarea for Form.Item label association. */
  id?: string;
  /** `data-testid` on the inner <textarea> element (reachable + fillable). */
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface TextAreaOutput {
  onChange?: (value: string) => void;
  /** Fires on blur — cancel-on-blur for inline-edit. */
  onBlur?: () => void;
  /** Raw keyboard handler for Escape / custom binding. */
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export type TextAreaProps = TextAreaInput & TextAreaOutput;

// ─── Component ────────────────────────────────────────────────────────────────

export function TextArea({
  value,
  placeholder,
  disabled = false,
  rows = 3,
  maxLength,
  autoFocus,
  status,
  id,
  testId,
  onChange,
  onBlur,
  onKeyDown,
}: TextAreaProps) {
  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange?.(e.target.value);
  }

  const props: AntTextAreaProps = {
    value,
    placeholder,
    disabled,
    rows,
    maxLength,
    autoFocus,
    status,
    id,
    onBlur,
    onKeyDown,
    onChange: handleChange,
    style: { width: "100%" },
    // `data-testid` flows through AntD's rest-props onto the inner <textarea>
    // element so `getByTestId(x).fill()` targets the fillable element.
    ...(testId ? { "data-testid": testId } : {}),
  };

  return <AntInput.TextArea {...props} />;
}
